import React from 'react'
import { Calendar, CalendarX } from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import EventCard from './EventCard'
import type { CalendarEvent } from '@/hooks/useCalendarEvents'

interface EventListProps {
  events: CalendarEvent[]
  selectedDate: Date
  loading?: boolean
  onEventClick?: (event: CalendarEvent) => void
}

const EventList: React.FC<EventListProps> = ({
  events,
  selectedDate,
  loading = false,
  onEventClick
}) => {
  const formattedDate = format(selectedDate, "EEEE, d 'de' MMMM", { locale: ptBR })

  if (loading) {
    return (
      <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-4 h-full">
        <div className="h-6 w-48 bg-neutral-800 rounded animate-pulse mb-4" />
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-neutral-800 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-4 h-full flex flex-col">
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="w-5 h-5 text-emerald-500" />
        <h3 className="text-lg font-semibold text-neutral-50 capitalize">
          {formattedDate}
        </h3>
      </div>

      {events.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
          <div className="w-16 h-16 bg-neutral-800 rounded-full flex items-center justify-center mb-4">
            <CalendarX className="w-8 h-8 text-neutral-500" />
          </div>
          <p className="text-neutral-400 text-sm">
            Nenhum compromisso para este dia
          </p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {events.map(event => (
            <EventCard
              key={event.id}
              event={event}
              onClick={() => onEventClick?.(event)}
            />
          ))}
        </div>
      )}

      {events.length > 0 && (
        <div className="mt-4 pt-3 border-t border-neutral-800">
          <p className="text-sm text-neutral-500">
            {events.length} compromisso{events.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  )
}

export default EventList
